import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'

import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { PasswordInput } from '@/components/PasswordInput'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { setIsAuthenticated } from '@/store/appSlice'
import { getRatesAction } from '@/store/packageSlice'

import packageJson from '../../../../package.json'
import { login } from '../../../api/auth/auth.api'
import { TextField } from '../../../components/TextField'
import { Button } from '../../../components/ui/button'
import { RoutePath } from '../../../RoutePath'
import LoginIcon from './login-icon.svg?react'

const initialFormState = {
  username: '',
  password: '',
}

export default function LoginPage() {
  const history = useHistory()
  const dispatch = useDispatch()
  const [formState, setFormState] = useState(initialFormState)
  const [isLoadingSignIn, setIsLoadingSignIn] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const { data: loggedUser } = useCurrentUser()
  const queryClient = useQueryClient()

  useEffect(() => {
    document.title = 'התחברות לאזור האישי | ShipTanbul'

    const logoutReason = localStorage.getItem('logout-reason')
    if (logoutReason === 'inactivity') {
      // Handle inactivity logout if needed
      localStorage.removeItem('logout-reason')
    }
  }, [])

  useEffect(() => {
    if (loggedUser) {
      history.push(RoutePath.ClientAddresses)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist()
    const { name, value } = e.target
    setFormState(prev => ({ ...prev, [name]: value }))
    setErrorMessage('')
  }

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const signIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      if (!formState.username) {
        setEmailError('יש למלא כתובת מייל')
        return
      }
      if (!isValidEmail(formState.username)) {
        setEmailError('כתובת מייל לא תקינה')
        return
      }
      if (!formState.password) {
        setPasswordError('יש למלא סיסמה')
        return
      }
      setIsLoadingSignIn(true)
      const { username, password } = formState

      const signinPromisResult = await login(username, password, packageJson.version)

      //update permissions
      let updatedPermission = { ...(loggedUser?.permissions || {}) }
      if (signinPromisResult.data.data['cognito:groups']) {
        signinPromisResult.data.data['cognito:groups'].forEach((group: string) => {
          updatedPermission = { ...updatedPermission, [group]: true }
        })
      }
      const user = {
        UserAttributes: signinPromisResult.data.data,
        Username: signinPromisResult.data.data.sub,
        groups: {
          Groups: signinPromisResult.data.data['cognito:groups'] ? signinPromisResult.data.data['cognito:groups'] : [],
        },
        permissions: updatedPermission,
      }

      //update redux & local storage
      queryClient.setQueryData([QUERY_KEYS.LOGGED_USER], user)
      localStorage.setItem('cognito-tokens', JSON.stringify(signinPromisResult.data.token))
      dispatch(setIsAuthenticated(true))
      // @ts-expect-error - TODO: fix this
      dispatch(getRatesAction())

      setIsLoadingSignIn(false)
      if (
        history.location.pathname === RoutePath.Login ||
        history.location.pathname === RoutePath.Welcome ||
        history.location.pathname === '/'
      ) {
        history.push(RoutePath.ClientAddresses)
      }
    } catch (error) {
      setIsLoadingSignIn(false)
      // Use the new error handling system
      const message = (error as { msg: string }).msg
      if (message) {
        setErrorMessage(message)
      } else {
        setErrorMessage('.בעיה בחיבור לרשת, אנא נסה שנית')
      }
    }
  }

  return (
    <div dir='rtl' data-twp className='flex items-center justify-center p-6'>
      <div className='flex w-full max-w-[480px] flex-col justify-center rounded-2xl bg-white p-6 shadow-sm'>
        {/* Welcome Message */}
        <div className='mb-6 text-center'>
          <div className='mx-auto mb-8 flex h-[140px] w-[140px] items-center justify-center'>
            <LoginIcon />
          </div>
          <h1 className='text-lg font-medium text-gray-900'>איזה כיף שחזרת!</h1>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className='mb-6 rounded-lg border border-red-200 bg-red-50 p-4'>
            <p className='text-center text-sm text-red-600'>{errorMessage}</p>
          </div>
        )}

        {/* Sign In Form */}
        <form onSubmit={signIn}>
          <div className='mb-2 flex flex-col gap-4'>
            <TextField
              label='כתובת מייל'
              type='email'
              value={formState.username}
              onChange={onChange}
              name='username'
              error={!!emailError}
              supportingText={emailError}
            />

            <PasswordInput
              value={formState.password}
              onChange={onChange}
              label='סיסמה'
              name='password'
              error={!!passwordError}
              supportingText={passwordError}
            />
          </div>

          <Link to={RoutePath.ForgotPassword}>
            <Button type='button' variant='link' className='mb-6 text-xs'>
              שכחת סיסמה?
            </Button>
          </Link>

          <Button type='submit' loading={isLoadingSignIn} disabled={isLoadingSignIn} size='lg' className='w-full'>
            התחבר
          </Button>

          {/* <Divider text='או' variant='withText' className='my-4' /> */}

          <div className='mt-4 space-y-4'>
            {/* @TODO google login is not supported yet */}
            {/* <Button variant='secondary' size='lg' disabled={isLoadingSignIn} trailingIcon={<GoogleIcon />} className='w-full font-medium'>
              התחבר עם חשבון Google
            </Button> */}

            <p className='text-center text-xs text-gray-600'>
              משתמש/ת חדש/ה?{' '}
              <a href='http://www.shiptanbul.com/start-here'>
                <Button type='button' variant='link' className='text-xs'>
                  הירשמ/י כאן
                </Button>
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}
